import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { LICENSE_TYPES, BUSINESS_TYPES, LICENSE_BY_TYPE } from "@/lib/constants";
import { Store, Truck, Factory, Warehouse, ArrowLeft, ArrowRight, Check, Upload, ShieldCheck, Clock } from "lucide-react";

const TYPE_ICONS = { manufacturer: Factory, distributor: Truck, wholesaler: Warehouse, retailer: Store };

const STEPS = ["Business Type", "Business Info", "Mobile Verification", "License & Documents"];

const pwValid = (p) => p.length >= 8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p);

const DEV_OTP_TOAST_MS = 8000;

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [f, setF] = useState({
    business_type: "", name: "", business_email: "", logo_file_id: null, owner_name: "", owner_mobile: "",
    address: "", license_type: "", license_number: "", license_issue_date: "",
    license_expiry_date: "", license_doc_file_id: null, gstin: "",
  });
  const gstinOk = !f.gstin.trim() || /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(f.gstin.trim().toUpperCase());
  const [logoName, setLogoName] = useState("");
  const [docName, setDocName] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const [otpRequired, setOtpRequired] = useState(true);
  useEffect(() => {
    api.get("/config").then((r) => setOtpRequired(!!r.data.signup_otp_required)).catch(() => {});
  }, []);

  const [emailTaken, setEmailTaken] = useState(false);
  const [mobileTaken, setMobileTaken] = useState(false);
  useEffect(() => {
    const email = f.business_email.trim();
    if (!email || !/@/.test(email)) { setEmailTaken(false); return; }
    const t = setTimeout(() => {
      api.get("/check-availability", { params: { email } }).then((r) => setEmailTaken(!!r.data.email_taken)).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [f.business_email]);
  useEffect(() => {
    const mobile = f.owner_mobile.trim();
    if (!mobile.startsWith("+") || mobile.length < 11) { setMobileTaken(false); return; }
    const t = setTimeout(() => {
      api.get("/check-availability", { params: { mobile } }).then((r) => setMobileTaken(!!r.data.mobile_taken)).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [f.owner_mobile]);

  const upload = async (file, purpose, setName, key) => {
    try {
      const res = await uploadFile(file, purpose);
      set(key, res.file_id);
      setName(res.filename);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const sendOtp = async () => {
    if (!f.owner_mobile.startsWith("+")) { toast.error("Use international format e.g. +9198..."); return; }
    setBusy(true);
    try {
      const res = await api.post("/otp/send", { mobile: f.owner_mobile });
      setOtpSent(true);
      if (res.data.dev_otp) toast.info(`Dev OTP: ${res.data.dev_otp}`, { duration: DEV_OTP_TOAST_MS });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      await api.post("/otp/verify", { mobile: f.owner_mobile, code: otp });
      setMobileVerified(true);
      toast.success("Mobile verified");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid OTP"); } finally { setBusy(false); }
  };

  const canNext = () => {
    if (step === 0) return !!f.business_type;
    if (step === 1) return f.name && f.owner_name && f.address && f.business_email && /@/.test(f.business_email) && pwValid(pw) && pw === pw2 && !emailTaken;
    if (step === 2) return (otpRequired ? mobileVerified : (f.owner_mobile.startsWith("+") && f.owner_mobile.length >= 11)) && !mobileTaken;
    if (step === 3) return f.license_type && f.license_number && f.license_issue_date && f.license_expiry_date && f.license_doc_file_id && gstinOk;
    return false;
  };

  const submit = async () => {
    if (new Date(f.license_issue_date) > new Date(f.license_expiry_date)) {
      toast.error("Issue date cannot be after expiry date"); return;
    }
    setBusy(true);
    try {
      const res = await api.post("/register", { ...f, password: pw, business_email: f.business_email, gstin: f.gstin.trim().toUpperCase() || null });
      login(res.data.token, res.data.user);
      setSubmitted(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Registration failed"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] rv-grain">
      <div className="mx-auto max-w-3xl px-6 py-10">
        <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#0D5C3A]"><ArrowLeft className="h-4 w-4" /> Home</Link>
        <h1 className="font-heading text-3xl font-extrabold text-stone-900">Register your business</h1>
        <p className="mt-1 text-stone-500">Four quick steps. You'll set up your team after approval.</p>

        <div className="mt-8 flex items-center gap-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-[#0D5C3A] text-white" : i === step ? "bg-[#C85A32] text-white" : "bg-[#EDE8DF] text-stone-500"}`}>
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <span className={`hidden text-xs font-semibold sm:inline ${i === step ? "text-stone-900" : "text-stone-400"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="h-px flex-1 bg-[#E5E0D8]" />}
            </React.Fragment>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
          className="mt-8 rounded-2xl border border-[#E5E0D8] bg-white p-7 shadow-sm">
          {step === 0 && (
            <div>
              <h2 className="font-heading text-lg font-semibold">What type of business are you?</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {BUSINESS_TYPES.map((bt) => ({ t: bt.value, icon: TYPE_ICONS[bt.value] || Store, label: bt.label, desc: bt.desc })).map((o) => (
                  <button key={o.t} data-testid={`register-type-${o.t}`} onClick={() => { set("business_type", o.t); set("license_type", ""); }}
                    className={`rounded-2xl border-2 p-6 text-left transition-all ${f.business_type === o.t ? "border-[#0D5C3A] bg-[#F0ECE1]" : "border-[#E5E0D8] hover:border-[#84A98C]"}`}>
                    <o.icon className={`h-8 w-8 ${f.business_type === o.t ? "text-[#0D5C3A]" : "text-stone-400"}`} />
                    <p className="mt-3 font-heading font-semibold">{o.label}</p>
                    <p className="text-sm text-stone-500">{o.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div><Label>Business Name *</Label><Input data-testid="register-name-input" className="mt-1" value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div><Label>Business Email *</Label><Input data-testid="register-email-input" type="email" className="mt-1" placeholder="used for password recovery & reminders" value={f.business_email} onChange={(e) => set("business_email", e.target.value)} />
                {emailTaken && <p className="mt-1 text-xs font-medium text-rose-600" data-testid="register-email-taken">This email is already registered. Try signing in instead.</p>}
              </div>
              <div><Label>Business Logo (optional)</Label>
                <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#E5E0D8] px-4 py-3 text-sm text-stone-500 hover:border-[#0D5C3A]">
                  <Upload className="h-4 w-4" /> {logoName || "Upload logo image"}
                  <input data-testid="register-logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "logo", setLogoName, "logo_file_id")} />
                </label>
              </div>
              <div><Label>Owner's Full Name *</Label><Input data-testid="register-owner-input" className="mt-1" value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} /></div>
              <div><Label>Business Address *</Label><Textarea data-testid="register-address-input" className="mt-1" value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>Create Password *</Label><Input data-testid="register-password-input" type="password" className="mt-1" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
                <div><Label>Re-enter Password *</Label><Input data-testid="register-password2-input" type="password" className="mt-1" value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
              </div>
              <p className={`text-xs ${pw && !pwValid(pw) ? "text-rose-600" : "text-stone-500"}`}>Min 8 characters with an uppercase, a lowercase and a number. You can later log in with OTP or this password.</p>
              {pw2 && pw !== pw2 && <p className="text-xs text-rose-600">Passwords do not match.</p>}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-heading text-lg font-semibold">{otpRequired ? "Verify your mobile number" : "Your mobile number"}</h2>
              <div><Label>Primary Mobile Number *</Label>
                <Input data-testid="register-mobile-input" className="mt-1" placeholder="+919812345678" value={f.owner_mobile} onChange={(e) => set("owner_mobile", e.target.value)} disabled={otpRequired && mobileVerified} />
                {mobileTaken && <p className="mt-1 text-xs font-medium text-rose-600" data-testid="register-mobile-taken">This mobile number is already registered. Try signing in instead.</p>}
              </div>
              {!otpRequired ? (
                <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-700" data-testid="register-otp-bypass-note">
                  Enter the mobile number you'll use to sign in. SMS verification is temporarily off — our team reviews and approves every profile before your account is activated.
                </p>
              ) : mobileVerified ? (
                <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700"><ShieldCheck className="h-4 w-4" /> Mobile verified</div>
              ) : !otpSent ? (
                <Button data-testid="register-send-otp-btn" disabled={busy} onClick={sendOtp} className="bg-[#0D5C3A] hover:bg-[#084229]">Send OTP</Button>
              ) : (
                <div className="flex items-end gap-2">
                  <div className="flex-1"><Label>Enter OTP</Label><Input data-testid="register-otp-input" className="mt-1 tracking-widest" value={otp} onChange={(e) => setOtp(e.target.value)} /></div>
                  <Button data-testid="register-verify-otp-btn" disabled={busy} onClick={verifyOtp} className="bg-[#0D5C3A] hover:bg-[#084229]">Verify</Button>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div><Label>Business License Type *</Label>
                <Select value={f.license_type} onValueChange={(v) => set("license_type", v)}>
                  <SelectTrigger data-testid="register-license-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{(LICENSE_BY_TYPE[f.business_type] || LICENSE_TYPES).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>License Number *</Label><Input data-testid="register-license-number" className="mt-1" value={f.license_number} onChange={(e) => set("license_number", e.target.value)} /></div>
              <div><Label>GSTIN (optional)</Label><Input data-testid="register-gstin-input" className="mt-1 uppercase" placeholder="29ABCDE1234F1Z5" maxLength={15} value={f.gstin} onChange={(e) => set("gstin", e.target.value)} />
                {!gstinOk && <p className="mt-1 text-xs font-medium text-rose-600" data-testid="register-gstin-invalid">Enter a valid 15-character GSTIN.</p>}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>Issue Date *</Label><Input data-testid="register-issue-date" type="date" className="mt-1" value={f.license_issue_date} onChange={(e) => set("license_issue_date", e.target.value)} /></div>
                <div><Label>Expiry Date *</Label><Input data-testid="register-expiry-date" type="date" className="mt-1" value={f.license_expiry_date} onChange={(e) => set("license_expiry_date", e.target.value)} /></div>
              </div>
              <div><Label>License Document * (PDF or image)</Label>
                <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#E5E0D8] px-4 py-3 text-sm text-stone-500 hover:border-[#0D5C3A]">
                  <Upload className="h-4 w-4" /> {docName || "Upload license document"}
                  <input data-testid="register-doc-input" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "license", setDocName, "license_doc_file_id")} />
                </label>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-between">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="register-back-btn">Back</Button>
            {step < STEPS.length - 1 ? (
              <Button data-testid="register-next-btn" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} className="bg-[#0D5C3A] hover:bg-[#084229]">Next <ArrowRight className="ml-2 h-4 w-4" /></Button>
            ) : (
              <Button data-testid="register-submit-btn" disabled={!canNext() || busy} onClick={submit} className="bg-[#C85A32] hover:bg-[#a8481f]">Submit for Verification</Button>
            )}
          </div>
        </motion.div>
      </div>

      <Dialog open={submitted} onOpenChange={() => {}}>
        <DialogContent className="max-w-md text-center" data-testid="register-submitted-dialog">
          <div className="mx-auto mt-2 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50">
            <Clock className="h-8 w-8 text-amber-500" />
          </div>
          <h2 className="mt-2 font-heading text-xl font-bold text-stone-900">Profile Under Review</h2>
          <p className="text-sm text-stone-500">
            Thanks! Your profile has been submitted for verification. Our team will review it shortly. You can close the app and sign back in anytime with your mobile number (OTP or password) to check your status. No payment is needed until you're approved.
          </p>
          <Button data-testid="register-submitted-ok-btn" onClick={() => navigate("/gate")} className="mt-2 w-full bg-[#0D5C3A] hover:bg-[#084229]">
            View Verification Status
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
